import React from 'react'
// libs
import { useDispatch } from 'react-redux'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useApolloClient } from 'hooks/useApolloClient'
// components
import PageLoader from 'components/UI/Loader/PageLoader'
// hooks
import { useInitialData } from 'hooks/useInitialData'
// types
import { IUser, IUserResponse } from 'types/graphql'
// constants
import { LICENCES_NAMES } from 'constants/licences'
// graphql
import { USER_ME_QUERY } from 'graphql/user/queries'
// store
import { setCurrentUser } from 'store/reducers/currentUser'
// utils
import { getAccessToken } from 'lib/auth'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

const InitialDataContainer: React.FC<any> = ({ children }) => {
  const user = useCurrentUser()
  const dispatch = useDispatch()

  const { client } = useApolloClient()
  const accessToken = getAccessToken()

  const {
    setRiskViews,
    setRiskTemplates,
    setObjectEntities,
    setSecurityProfiles,
    setObjectSecurities,
    setCompanyResponseTypes
  } = useInitialData()

  const [initialDataFetched, setInitialDataFetched] = React.useState(false)

  const getInitialData = async () => {
    try {
      const { data } = await client.query<{ userMe: IUserResponse }>({ query: USER_ME_QUERY })

      const user: IUser = data?.userMe.data

      if (user) {
        await setObjectEntities()
        await setSecurityProfiles()
        await setObjectSecurities()
        await setCompanyResponseTypes()

        const canViewRisks = user.company?.licences?.find(({ name }) => name === LICENCES_NAMES.FORMS)?.active

        if (canViewRisks) {
          await setRiskViews()
          await setRiskTemplates()
        }

        dispatch(setCurrentUser(user))
      }
    } catch (error) {
      console.log('[InitialDataContainer.getInitialData]: ', parseGraphQLErrorMessages(error))
    } finally {
      setInitialDataFetched(true)
    }
  }

  React.useLayoutEffect(() => {
    if (accessToken) {
      getInitialData()
    }
  }, [!!accessToken])

  React.useEffect(() => {
    if (!user.isAuthorized) {
      setInitialDataFetched(false)
    }
  }, [user?.isAuthorized])

  if (!initialDataFetched) {
    return <PageLoader isLoading />
  }

  return children
}

export default InitialDataContainer
